import React, { useCallback } from 'react';

import { Card } from '@/components/ui/cards';
import { Selector, type SelectorOption } from '@/components/ui/inputs/selector';
import { PlaceholderContent } from '@/components/ui/layout';
import { useTranslation } from '@/i18n';
import { useCompanyDashboardFilters } from '@/hooks/attendance/companyDashboard/useCompanyDashboardFilters';


// "Empresa" tab content (ASIS-5638 / REXASIS-001 — first ticket of the epic). Owns
// the 4-filter panel (Secciones / Empresa / Área / Colaboradores). The
// "Situación de asistencia" and "Resumen del mes" sections themselves ship in later
// tickets — for now this renders a single placeholder below the filters panel.
export const CompanyTabContent: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    isLoading,
    isAreasLoading,
    isEmployeesLoading,
    isLoadingMoreEmployees,
    employeeHasMore,
    loadMoreEmployees,
    filters,
    catalogOptions,
    handlers,
  } = useCompanyDashboardFilters();

  const allLabel = translate('attendance:dashboard.company.filters.all');
  const allCompaniesLabel = translate('attendance:dashboard.company.filters.allCompanies');
  const allAreasLabel = translate('attendance:dashboard.company.filters.allAreas');
  const allEmployeesLabel = translate('attendance:dashboard.company.filters.allEmployees');

  const formatSectionCollapsedLabel = useCallback((selected: SelectorOption[]) => {
    const rest = selected.length - 1;
    return rest > 0
      ? translate('attendance:dashboard.company.filters.firstPlusRest', { label: selected[0]?.label ?? '', rest })
      : (selected[0]?.label ?? '');
  }, [translate]);

  const formatEmployeeCollapsedLabel = useCallback((selected: SelectorOption[]) => (
    translate('attendance:dashboard.company.filters.selectedMasculine', { count: selected.length })
  ), [translate]);

  return (
    <div className="flex flex-col gap-6" data-testid="company-dashboard-tab-content">
      <Card elevation={2} className="rounded-2xl" data-testid="company-dashboard-filters-panel">
        <div className="grid grid-cols-1 gap-6 p-4 sm:grid-cols-4">
          <Selector
            label={translate('attendance:dashboard.company.filters.section')}
            value={filters.sectionCodes}
            options={catalogOptions.sectionOptions}
            onChange={handlers.onSectionChange}
            multiple
            searchable
            formatCollapsedLabel={formatSectionCollapsedLabel}
            isLoading={isLoading}
            placeholder={allLabel}
            data-testid="company-dashboard-section-selector"
          />
          <Selector
            label={translate('attendance:dashboard.company.filters.company')}
            value={filters.companyIds.map(String)}
            options={catalogOptions.companyOptions}
            onChange={handlers.onCompanyChange}
            multiple
            searchable
            isLoading={isLoading}
            placeholder={allCompaniesLabel}
            data-testid="company-dashboard-company-selector"
          />
          <Selector
            label={translate('attendance:dashboard.company.filters.area')}
            value={filters.areaIds.map(String)}
            options={catalogOptions.areaOptions}
            onChange={handlers.onAreaChange}
            multiple
            searchable
            isLoading={isAreasLoading}
            placeholder={allAreasLabel}
            data-testid="company-dashboard-area-selector"
          />
          <Selector
            label={translate('attendance:dashboard.company.filters.employee')}
            value={filters.employeeIds.map(String)}
            options={catalogOptions.employeeOptions}
            onChange={handlers.onEmployeeChange}
            onLoadMore={loadMoreEmployees}
            hasNextPage={employeeHasMore}
            isLoadingMore={isLoadingMoreEmployees}
            multiple
            searchable
            formatCollapsedLabel={formatEmployeeCollapsedLabel}
            isLoading={isLoading || isAreasLoading || isEmployeesLoading}
            placeholder={allEmployeesLabel}
            data-testid="company-dashboard-employee-selector"
          />
        </div>
      </Card>

      <Card elevation={1} className="rounded-2xl" data-testid="company-dashboard-sections-placeholder">
        <PlaceholderContent label={translate('attendance:dashboard.company.contentPending')} />
      </Card>
    </div>
  );
};

export default CompanyTabContent;
